import { FC } from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { toast } from 'react-toastify';
import { spacing, colors, fontStyles } from '../../styles';
import { InvestorType } from '../../utils/smartChatPrompts';

interface InvestorPersonaOption {
  value: InvestorType;
  label: string;
  toastMessage: string;
}

const INVESTOR_PERSONA_OPTIONS: InvestorPersonaOption[] = [
  {
    value: 'other',
    label: 'Default',
    toastMessage: 'Our chat AI will give you balanced answers on any topic',
  },
  {
    value: 'new-investor',
    label: 'Teacher',
    toastMessage: 'Our chat AI will explain concepts step by step',
  },
  {
    value: 'active-trader',
    label: 'Trader',
    toastMessage: 'Our chat AI will focus on charts, technicals, and setups',
  },
  {
    value: 'researcher',
    label: 'Researcher',
    toastMessage: 'Our chat AI will focus on earnings and fundamentals',
  },
];

const STORAGE_KEY = 'investorPersona';
const ONBOARDING_KEY = 'onboardingMotivations';

export const getInvestorPersona = (): InvestorType | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return stored as InvestorType;
    }

    const onboardingStored = localStorage.getItem(ONBOARDING_KEY);
    if (onboardingStored) {
      const parsed = JSON.parse(onboardingStored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const persona = parsed[0] as InvestorType;
        localStorage.setItem(STORAGE_KEY, persona);
        return persona;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

export const setInvestorPersona = (persona: InvestorType): void => {
  localStorage.setItem(STORAGE_KEY, persona);
};

interface InvestorPersonaSelectorProps {
  selectedPersona: InvestorType | null;
  onChange: (persona: InvestorType) => void;
}

const InvestorPersonaSelectorComponent: FC<InvestorPersonaSelectorProps> = ({ selectedPersona, onChange }) => {
  const handleSelect = (value: InvestorType) => {
    if (value === selectedPersona) return;

    const selectedOption = INVESTOR_PERSONA_OPTIONS.find((o) => o.value === value);
    if (selectedOption) {
      toast(selectedOption.toastMessage);
    }

    posthog.capture('Investor Persona Changed', {
      previousPersona: selectedPersona,
      newPersona: value,
      source: 'settings',
    });
    onChange(value);
  };

  return (
    <div
      css={css`
        display: flex;
        flex: 1;
        gap: ${spacing.milli};
        padding: ${spacing.micro};
        background: ${colors.fog};
        border-radius: 12px;
        border: 0.5px solid ${colors.liquorice}20;

        html[data-theme='dark'] & {
          background: rgba(80, 78, 75, 0.3);
          border-color: ${colors.cloud}30;
        }
      `}
    >
      {INVESTOR_PERSONA_OPTIONS.map((option) => (
        <button
          key={option.value}
          onClick={() => handleSelect(option.value)}
          css={css`
            flex: 1;
            min-width: 0;
            padding: ${spacing.centi} ${spacing.milli};
            border: none;
            border-radius: 10px;
            background: ${selectedPersona === option.value ? 'white' : 'transparent'};
            color: ${selectedPersona === option.value ? 'var(--text-color)' : colors.silver};
            box-shadow: ${selectedPersona === option.value ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none'};
            ${fontStyles.flea};
            font-weight: ${selectedPersona === option.value ? 500 : 400};
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
              color: var(--text-color);
            }

            html[data-theme='dark'] & {
              background: ${selectedPersona === option.value ? colors.liquorice : 'transparent'};
              color: ${selectedPersona === option.value ? 'var(--text-color)' : colors.cloud};
            }
          `}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export const InvestorPersonaSelector = InvestorPersonaSelectorComponent;
